import { Box, Heading } from "@chakra-ui/react";
import React from "react";
import Select, { SingleValue } from "react-select";

import {
  Button,
  Card,
  CardTitle,
  SelectOptionalDateRange,
} from "../../../components";
import { SelectOptionalDateRangeState } from "../../../components/SelectOptionalDateRange/SelectOptionalDateRange";
import { SelectedOption } from "./utils";

type GroupedOption = {
  label: string;
  options: SelectedOption[];
};

interface SearchFiltersProps {
  // Current filter values
  captureStatus?: SelectedOption | null;
  recordingReason?: SelectedOption | null;
  recordingStatus?: SelectedOption | null;
  position?: SelectedOption | null;
  department?: SelectedOption | null;
  dateRange: SelectOptionalDateRangeState;

  // Filter options
  captureStatusOptions?: SelectedOption[];
  recordingReasonOptions?: GroupedOption[];
  recordingStatusOptions?: SelectedOption[];
  positionOptions?: SelectedOption[];
  departmentOptions?: SelectedOption[];

  // Change handlers
  onCaptureStatusChange: (selectedOption: SingleValue<SelectedOption>) => void;
  onRecordingReasonChange: (
    selectedOption: SingleValue<SelectedOption>
  ) => void;
  onRecordingStatusChange: (
    selectedOption: SingleValue<SelectedOption>
  ) => void;
  onPositionChange: (selectedOption: SingleValue<SelectedOption>) => void;
  onDepartmentChange: (selectedOption: SingleValue<SelectedOption>) => void;
  onDateRangeChange: (dateRange: SelectOptionalDateRangeState) => void;
  onClearAllFilters: () => void;

  // UI state
  loading?: boolean;
  maxDate?: Date;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  captureStatus,
  recordingReason,
  recordingStatus,
  position,
  department,
  dateRange,
  captureStatusOptions = [],
  recordingReasonOptions = [],
  recordingStatusOptions = [],
  positionOptions = [],
  departmentOptions = [],
  onCaptureStatusChange,
  onRecordingReasonChange,
  onRecordingStatusChange,
  onPositionChange,
  onDepartmentChange,
  onDateRangeChange,
  onClearAllFilters,
  loading = false,
  maxDate,
}) => {
  return (
    <Card p={0} pt={0}>
      <CardTitle position="relative">
        Filters
        <Button
          variant="outline"
          size="xs"
          position="absolute"
          top="10px"
          right="3"
          onClick={onClearAllFilters}
          isDisabled={
            !captureStatus &&
            !recordingReason &&
            !recordingStatus &&
            !position &&
            !department
          }
        >
          Clear
        </Button>
      </CardTitle>

      {/* Position Filter */}
      <Box pl={4} pr={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={2}>
          Position
        </Heading>
        <Select
          placeholder="Select position"
          value={position}
          onChange={onPositionChange}
          isDisabled={loading}
          aria-label="Filter by position"
          options={positionOptions}
          isClearable
        />
      </Box>

      {/* Department Filter */}
      <Box pl={4} pr={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={2}>
          Department
        </Heading>
        <Select
          placeholder="Select department"
          value={department}
          onChange={onDepartmentChange}
          isDisabled={loading}
          aria-label="Filter by department"
          options={departmentOptions}
          isClearable
        />
      </Box>

      {/* Recording Outcome Filter */}
      <Box pl={4} pr={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={2} mt={2}>
          Recording outcome
        </Heading>
        <Select
          placeholder="Select recording outcome"
          value={captureStatus}
          onChange={onCaptureStatusChange}
          isDisabled={loading}
          aria-label="Filter by recording outcome"
          options={captureStatusOptions}
          isClearable
        />
      </Box>

      {/* Recording Status Filter */}
      <Box pl={4} pr={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={2}>
          Recording status
        </Heading>
        <Select
          placeholder="Select recording status"
          value={recordingStatus}
          onChange={onRecordingStatusChange}
          isDisabled={loading}
          aria-label="Filter by recording status"
          options={recordingStatusOptions}
          isClearable
        />
      </Box>

      {/* Recording Reason Filter */}
      <Box pl={4} pr={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={2}>
          Recording reason
        </Heading>
        <Select
          placeholder="Select recording reason"
          value={recordingReason}
          onChange={onRecordingReasonChange}
          isDisabled={loading}
          aria-label="Filter by recording reason"
          options={recordingReasonOptions}
          isClearable
        />
      </Box>

      {/* Date Range Filter */}
      <Box pl={4} pr={4} pb={4} mt={4}>
        <Heading as="h5" fontSize="sm" mb={1}>
          Date range
        </Heading>
        <SelectOptionalDateRange
          disabled={loading}
          state={dateRange}
          onSelect={onDateRangeChange}
          maxDate={maxDate}
          layout="vertical"
          showLabel={false}
          includeFutureOptions
        />
      </Box>
    </Card>
  );
};

export default SearchFilters;
